'use client';

import { datadogErrorHelper, Drawer } from '@bts-web/core-features/common';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { TFunction } from 'i18next';
import { CryptoTransfersVASPQuery } from '@bts-web/data-layer/server';
import { PrecisionFloat, type TimeScalar } from '@bts-web/utils-formatting';
import { SelfDeclarationScreen } from './SelfDeclarationScreen/SelfDeclarationScreen';
import { getCryptoTransferTranslations } from '../../utils/getCryptoTransferTranslations';
import { SelfDeclarationScreenSkeleton } from './SelfDeclarationScreenSkeleton/SelfDeclarationScreenSkeleton';
import { useEffect, useState } from 'react';
import {
  getCryptoTransfersTransactions,
  getCryptoTransfersVASPList,
  revalidateCryptoTransfersTransactions,
} from '../../../common/gqlActions';
import { useHandleGqlErrorsWithSnackbar } from '../../../common/utils/errors';
import {
  CryptoTransfersTransactionsAssetType,
  CryptoTransfersTransactionsExternalAddressType,
} from '../../../transaction-details/types';
import { TransferNetworkDataType } from '../../utils/useTransfersNextStepState.client';
import { useDrawerMetadata } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';

export const transactionDetailsDrawerId = 'transaction_details_drawer';

export type SelfDeclarationData = {
  assetData: CryptoTransfersTransactionsAssetType | null;
  networkData: TransferNetworkDataType | null;
  externalAddress: CryptoTransfersTransactionsExternalAddressType;
  createdAt: TimeScalar | null;
  assetAmount: PrecisionFloat;
};

export type SelfDeclarationDepositDrawerPropsWithTransactionId = {
  transactionId: string;
  cryptoTransferData?: never;
  notificationId?: string;
  finishWalletUpdate?: never;
  refetchCryptoTransfersTransactions?: () => void;
};

export type SelfDeclarationDepositDrawerPropsWithData = {
  cryptoTransferData: SelfDeclarationData;
  transactionId?: never;
  notificationId?: never;
  refetchCryptoTransfersTransactions?: () => void;
};

type SelfDeclarationDrawerProps =
  | SelfDeclarationDepositDrawerPropsWithTransactionId
  | SelfDeclarationDepositDrawerPropsWithData;

export const SelfDeclarationDepositDrawer = ({
  transactionId,
  cryptoTransferData,
  notificationId,
  refetchCryptoTransfersTransactions,
}: SelfDeclarationDrawerProps) => {
  const [selfDeclarationData, setSelfDeclarationData] = useState<{
    walletHosts: CryptoTransfersVASPQuery['response']['cryptoTransfersVASP'];
    cryptoTransferData: SelfDeclarationData;
    loading: boolean;
  }>({
    walletHosts: null,
    cryptoTransferData: {
      assetData: null,
      networkData: null,
      externalAddress: null,
      createdAt: null,
      assetAmount: null,
    },
    loading: true,
  });

  const { closeThisDrawer } = useDrawerMetadata();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { t } = useClientTranslation();

  const handleFinishWalletUpdate = async () => {
    await revalidateCryptoTransfersTransactions();

    closeThisDrawer();

    // update transaction in the transaction drawer on client
    if (refetchCryptoTransfersTransactions) {
      refetchCryptoTransfersTransactions();
    }
  };

  const fetchData = async () => {
    try {
      const walletHostsResponse = await getCryptoTransfersVASPList();

      const walletHosts = walletHostsResponse?.data.cryptoTransfersVASP ?? null;

      if (walletHostsResponse?.errors) {
        processErrors(walletHostsResponse.errors);
      }

      if (transactionId) {
        const response = await getCryptoTransfersTransactions({
          id: transactionId,
        });

        const transactionDataObjContainer =
          response?.data?.cryptoTransfersTransactions?.edges;

        const cryptoTransferTransactionNode = transactionDataObjContainer
          ? transactionDataObjContainer[0]?.node
          : null;

        if (response?.errors) {
          processErrors(response.errors);
        }

        setSelfDeclarationData({
          walletHosts,
          cryptoTransferData: {
            assetData: cryptoTransferTransactionNode?.asset || null,
            networkData:
              (cryptoTransferTransactionNode?.network as TransferNetworkDataType) ||
              null,
            externalAddress:
              cryptoTransferTransactionNode?.externalAddress || null,
            createdAt: cryptoTransferTransactionNode?.createdAt ?? null,
            assetAmount: cryptoTransferTransactionNode?.assetAmount,
          },
          loading: false,
        });

        return;
      }

      setSelfDeclarationData({
        walletHosts,
        cryptoTransferData: {
          assetData: null,
          networkData: null,
          externalAddress: null,
          createdAt: null,
          assetAmount: null,
        },
        loading: false,
      });
    } catch (error) {
      setSelfDeclarationData({
        walletHosts: null,
        cryptoTransferData: {
          assetData: null,
          networkData: null,
          externalAddress: null,
          createdAt: null,
          assetAmount: null,
        },
        loading: false,
      });

      datadogErrorHelper({
        errorMessage: JSON.stringify(error),
        errorSeverity: DatadogErrorLevels.HIGH,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const translations = getCryptoTransferTranslations(t as TFunction);

  const { assetData, externalAddress, createdAt, assetAmount, networkData } =
    cryptoTransferData || selfDeclarationData?.cryptoTransferData || {};

  return (
    <Drawer title={translations.cryptoDepositOnHold}>
      {selfDeclarationData.loading ? (
        <SelfDeclarationScreenSkeleton />
      ) : (
        <SelfDeclarationScreen
          walletHosts={selfDeclarationData.walletHosts}
          assetData={assetData}
          networkData={networkData}
          externalAddress={externalAddress}
          createdAt={createdAt}
          assetAmount={assetAmount}
          finishWalletUpdate={handleFinishWalletUpdate}
          notificationId={notificationId}
          onClose={closeThisDrawer}
        />
      )}
    </Drawer>
  );
};
